import { ENVIRONMENTS, environmentById, type EnvironmentDef } from '../config/environments';
import { t, tId } from './i18n';

function el<T extends HTMLElement = HTMLElement>(selector: string): T {
  const found = document.querySelector<T>(selector);
  if (!found) throw new Error(`Missing element ${selector}`);
  return found;
}

/** True when an environment is open: no unlock rule, or the required distance was reached in the previous one. */
export function isUnlocked(env: EnvironmentDef, bestOf: (envId: string) => number): boolean {
  return env.unlock === null || bestOf(env.unlock.after) >= env.unlock.distance;
}

/** The map overlay: one card per environment with its best distance or what unlocks it. */
export class MapScreen {
  private readonly overlay = el('#map-overlay');
  private readonly list = el('#map-list');
  onPick: (env: EnvironmentDef) => void = () => {};
  onClose: () => void = () => {};

  constructor() {
    for (const env of ENVIRONMENTS) {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'map-card';
      card.dataset.env = env.id;
      card.style.setProperty('--sky-top', env.palette.skyTop);
      card.style.setProperty('--sky-low', env.palette.skyHorizon);
      card.style.setProperty('--ground', env.palette.ground);
      card.innerHTML = `
        <span class="map-swatch" aria-hidden="true"></span>
        <span class="map-text"><b>${env.name}</b><small></small></span>
        <span class="map-status"></span>`;
      card.addEventListener('click', () => {
        if (!card.classList.contains('is-locked')) this.onPick(env);
      });
      this.list.append(card);
    }
    el('#map-close').addEventListener('click', () => this.onClose());
  }

  get visible(): boolean {
    return !this.overlay.hidden;
  }

  show(currentId: string, bestOf: (envId: string) => number): void {
    for (const card of this.list.querySelectorAll<HTMLButtonElement>('.map-card')) {
      const env = environmentById(card.dataset.env!)!;
      const open = isUnlocked(env, bestOf);
      const status = card.querySelector('.map-status')!;
      card.querySelector('.map-text small')!.textContent = tId(`env.${env.id}`, env.blurb);
      card.classList.toggle('is-locked', !open);
      card.classList.toggle('is-current', env.id === currentId);
      card.setAttribute('aria-disabled', String(!open));
      if (open) {
        const best = Math.floor(bestOf(env.id));
        status.textContent = env.id === currentId ? t('map.driving') : best > 0 ? t('map.best', { m: best }) : t('map.new');
      } else {
        const after = environmentById(env.unlock!.after)!;
        status.textContent = t('map.reach', { m: env.unlock!.distance, env: after.name });
      }
    }
    this.overlay.hidden = false;
    el('#map-close').focus();
  }

  hide(): void {
    this.overlay.hidden = true;
  }
}
